import { useEffect, useState, useMemo, useCallback } from "react"
import { Download, Eye, Copy, Send, Pencil, Loader2 } from "lucide-react"

import RowOptions from "../../../table/row-options"
import {
  useLazyFetchSubscriptionDetailsQuery,
  useSendIntroEmailMutation,
  useCreateRateflowPriceMutation
} from "../../../../../redux/queries"
import { useAppDispatch } from "../../../../hooks"
import { pushToast } from "../../../../../redux/slices/notifications"
import { SubscriptionDetails, SubscriptionLogV2 } from "../../../../../types"
import { handleLoanSummaryDownload } from "../../../../utils/handleLoanSummaryDownload"

interface Props {
  info: SubscriptionLogV2
  onViewLeadDashboard: (link: string) => void
  onCopyDashboardLink: (link: string) => void
  onEdit: (id: string) => void
}

const MenuCell: React.FC<Props> = ({
  info,
  onViewLeadDashboard,
  onCopyDashboardLink,
  onEdit
}) => {
  const dispatch = useAppDispatch()
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false)

  const [fetchDetails] = useLazyFetchSubscriptionDetailsQuery()
  const [
    createRateflowPrice,
    {
      data: newRateflowPrice,
      isLoading: isLoadingRateflowPrice,
      isError: isErrorRateflowPriceApi
    }
  ] = useCreateRateflowPriceMutation()

  const isErrorRateflowPrice =
    isErrorRateflowPriceApi ||
    (newRateflowPrice && "status" in newRateflowPrice)

  const [
    sendIntroEmail,
    { isLoading: isSendingIntroEmail, isError: isErrorIntroEmail }
  ] = useSendIntroEmailMutation()

  const isLoading = useMemo(
    () => isLoadingRateflowPrice || isSendingIntroEmail || isDownloadingPdf,
    [isLoadingRateflowPrice, isSendingIntroEmail, isDownloadingPdf]
  )

  const isError = useMemo(
    () => isErrorRateflowPrice || isErrorIntroEmail,
    [isErrorRateflowPrice, isErrorIntroEmail]
  )

  const handleSendIntroEmail = useCallback(
    (log: SubscriptionLogV2) => {
      sendIntroEmail(log.id)
    },
    [sendIntroEmail]
  )

  const handleDownloadLoanEstimate = useCallback(
    async (data: SubscriptionDetails) => {
      setIsDownloadingPdf(true)
      try {
        // First create the rateflow price
        const rateflowResult = await createRateflowPrice({
          ...data.quote_info,
          loid: data.loid
        }).unwrap()

        if (
          rateflowResult &&
          Array.isArray(rateflowResult) &&
          rateflowResult[0]?.quote_id
        ) {
          // Then download the PDF using the new utility
          const success = await handleLoanSummaryDownload(
            data.id,
            rateflowResult[0].quote_id.toString(),
            `loan-summary-${data.id}.pdf`
          )

          if (!success) {
            dispatch(
              pushToast({
                msg: "Failed to download loan summary",
                theme: "danger",
                timeout: 3000
              })
            )
          }
        }
      } catch (error) {
        dispatch(
          pushToast({
            msg: "Failed to generate loan summary",
            theme: "danger",
            timeout: 3000
          })
        )
      } finally {
        setIsDownloadingPdf(false)
      }
    },
    [createRateflowPrice, dispatch]
  )

  const handleDownloadClick = useCallback(async () => {
    setIsDownloadingPdf(true)
    try {
      const result = await fetchDetails({ id: info.id }).unwrap()
      if (result) {
        await handleDownloadLoanEstimate(result)
      }
    } catch {
      dispatch(
        pushToast({
          msg: "Failed to fetch subscription details",
          theme: "danger",
          timeout: 3000
        })
      )
      setIsDownloadingPdf(false)
    }
  }, [fetchDetails, info.id, handleDownloadLoanEstimate, dispatch])

  useEffect(() => {
    if (isError) {
      dispatch(
        pushToast({
          msg: "Something went wrong, please try again.",
          theme: "danger",
          timeout: 3000
        })
      )
    }
  }, [dispatch, isError])

  const dashboardLink = info.dashboard_link ?? ""

  const actions = useMemo(
    () => [
      {
        label: "View dashboard",
        icon: Eye,
        onClick: () => onViewLeadDashboard(dashboardLink)
      },
      {
        label: "Copy dashboard link",
        icon: Copy,
        onClick: () => onCopyDashboardLink(dashboardLink)
      },
      {
        label: "Download loan estimate",
        icon: Download,
        onClick: handleDownloadClick
      },
      {
        label: "Send intro email to lead",
        icon: Send,
        onClick: () => handleSendIntroEmail(info)
      },
      {
        label: "Edit subscription",
        icon: Pencil,
        onClick: () => onEdit(info.id)
      }
    ],
    [
      dashboardLink,
      onViewLeadDashboard,
      onCopyDashboardLink,
      handleDownloadClick,
      handleSendIntroEmail,
      onEdit,
      info
    ]
  )

  return (
    <td className="relative whitespace-nowrap py-4 px-0 text-right text-sm font-medium sm:pr-6 pl-3 pr-4">
      {isLoading ? (
        <Loader2
          className="animate-spin h-6 w-6 text-primary-alt-400 ml-auto"
          aria-hidden="true"
        />
      ) : (
        <RowOptions actions={actions} />
      )}
    </td>
  )
}
export default MenuCell
